import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Typography,
  Box,
  CircularProgress,
} from "@material-ui/core";
import {
  validateUsernameChange,
  validateUsernameFormat,
  formatNextChangeDate,
  User,
  UsernameChangeValidation,
} from "../utils/usernameValidation";
import { changeUsernameAPI } from "../api/userApi";
import { showToast } from "../utils/toast";

interface UsernameChangeDialogProps {
  open: boolean;
  onClose: () => void;
  user: User;
  onUsernameChanged: (newUsername: string) => void;
}

const UsernameChangeDialog: React.FC<UsernameChangeDialogProps> = ({
  open,
  onClose,
  user,
  onUsernameChanged,
}) => {
  const [newUsername, setNewUsername] = useState("");
  const [validation, setValidation] = useState<UsernameChangeValidation | null>(
    null
  );
  const [isLoading, setIsLoading] = useState(false);
  const [formatError, setFormatError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setNewUsername(user.username);
      const validationResult = validateUsernameChange(user);
      setValidation(validationResult);
      setFormatError(null);

      // Show toast for cooldown status
      if (!validationResult.canChange && validationResult.reason) {
        showToast(validationResult.reason, "warning");
      }
    }
  }, [open, user]);

  const handleUsernameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setNewUsername(value);

    // Validate format
    const formatValidation = validateUsernameFormat(value);
    setFormatError(formatValidation.error || null);
  };

  const handleSubmit = async () => {
    if (
      !validation?.canChange ||
      formatError ||
      newUsername === user.username
    ) {
      return;
    }

    setIsLoading(true);
    try {
      const response = await changeUsernameAPI(newUsername);
      if (response.success) {
        showToast("Username changed successfully!", "success");
        onUsernameChanged(newUsername);
        onClose();
      } else {
        showToast(response.error || "Failed to change username", "error");
      }
    } catch (error) {
      console.error("Username change error:", error);
      showToast(
        (error as Error)?.message || "Failed to change username",
        "error"
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    if (!isLoading) {
      onClose();
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Typography variant="h6" style={{ color: "white" }}>
          Change Username
        </Typography>
      </DialogTitle>

      <DialogContent>
        <Box mb={2}>
          <Typography variant="body2" style={{ color: "white" }}>
            {user.contentProviderAgreementAccepted
              ? "As a creator, you can change your username once every 60 days to maintain brand consistency."
              : "You can change your username once every 14 days."}
          </Typography>
        </Box>

        {validation && !validation.canChange && validation.nextChangeDate && (
          <Box
            mb={2}
            p={2}
            style={{
              backgroundColor: "#fff3cd",
              borderRadius: "4px",
              border: "1px solid #ffeaa7",
            }}
          >
            <Typography variant="body2" style={{ color: "white" }}>
              Next change available:{" "}
              {formatNextChangeDate(validation.nextChangeDate)}
            </Typography>
          </Box>
        )}

        <TextField
          fullWidth
          label="New Username"
          value={newUsername}
          onChange={handleUsernameChange}
          error={!!formatError}
          helperText={
            formatError ||
            "Username must be 3-20 characters, letters, numbers, underscores, and hyphens only"
          }
          disabled={!validation?.canChange || isLoading}
          variant="outlined"
          margin="normal"
          InputProps={{
            style: { color: "white" },
          }}
          InputLabelProps={{
            style: { color: "white" },
          }}
          FormHelperTextProps={{
            style: { color: "white" },
          }}
        />

        {validation?.canChange && (
          <Box
            mt={2}
            p={2}
            style={{
              backgroundColor: "#d1ecf1",
              borderRadius: "4px",
              border: "1px solid #bee5eb",
            }}
          >
            <Typography variant="body2">
              ⚠️ Changing your username will update all your profile links. Old
              links will automatically redirect to your new username.
            </Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={isLoading}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          color="primary"
          disabled={
            !validation?.canChange ||
            formatError !== null ||
            newUsername === user.username ||
            isLoading
          }
          startIcon={isLoading ? <CircularProgress size={20} /> : null}
        >
          {isLoading ? "Changing..." : "Change Username"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default UsernameChangeDialog;
